import { ProcedureInstanceBase, PROCEDURETYPE } from "@/backend/models/procedureInstances/procedureInstanceBase";
import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BasicButton } from "../buttons/BasicButton";
import { LoadingModal } from "../LoadingModal";
import { useFunctionalInterfacesStore } from "@/frontend/store/FunctionalInterfaceStore";
import { Modal, Offcanvas } from "react-bootstrap";
import { ProcedureImplementationSetupWizard } from "./ProcedureImplementationSetupWizard";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { ParameterType } from "@/types/parameterTypes";
import { ProcedureCreateParameters } from "./ProcedureCreateParameters";
import { LoadingSpinner } from "../LoadingSpinner";
import { useGlobalStore } from "@/frontend/store/GlobalStore";

export function RunPreviewButton() {
  const location = useLocation();
  const procedure = useProcedureInstanceStore((state) => state.procedureInstance) as ProcedureInstanceBase;
  const generateFromTemplate = useTreeStore((state) => state.generateFromTemplate);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showSelectFunctionalInterfaceModal, setShowSelectFunctionalInterfaceModal] = useState(false);
  const [generateProcessing, setGenerateProcessing] = useState(false);
  const functionalInterfaceVersions = useFunctionalInterfacesStore(state => state.functionalInterfaceVersions);
  const user = useGlobalStore((state) => state.userClass);

  // Generating rewrites the template's generated content, which only the org that owns
  // the template may do (the API refuses anyone else, e.g. an enterprise it is published to).
  if (procedure.procedureType !== PROCEDURETYPE.TEMPLATE || procedure.orgId !== user?.getOrgId()) {
    return null;
  }

  async function generateAction() {
    setShowGenerateModal(false);
    if(functionalInterfaceVersions.length !== 0) {
      setShowSelectFunctionalInterfaceModal(true);
      return;
    }
    setGenerateProcessing(true);
    await generateFromTemplate(procedure.id, {}).finally(() => {
      setGenerateProcessing(false);
    });
  };

  async function generateFromTemplateAction(assignments: { [key: string]: string }) {
    setShowSelectFunctionalInterfaceModal(false);
    setGenerateProcessing(true);
    await generateFromTemplate(procedure.id, assignments).finally(() => {
      setGenerateProcessing(false);
    });
  }

  return (
    <>
      { location.pathname.includes('scaffold') && <BasicButton
        className="btn btn-sm btn-primary pe-auto"
        onClick={() => setShowGenerateModal(true)}
        label={I18n.get('applyScaffold')}
      />}
      {generateProcessing && <LoadingModal message={I18n.get('applyingScaffold')} />}
      {showGenerateModal && <GenerateModal setShowConfirmModal={setShowGenerateModal} procedureId={procedure.id} generateAction={generateAction} />}
      {showSelectFunctionalInterfaceModal && <Modal show onHide={() => setShowSelectFunctionalInterfaceModal(false)}>
        <Modal.Body>
          <ProcedureImplementationSetupWizard generateFunction={generateFromTemplateAction} />
          <div className="d-flex justify-content-end mt-2">
            <BasicButton label={I18n.get('cancel')} className='fusa-btn-medium btn-blue-light' onClick={() => setShowSelectFunctionalInterfaceModal(false)} />
          </div>
        </Modal.Body>
      </Modal>}
    </>
  );
}

export function GenerateModal(
  props: {
    setShowConfirmModal: (b: boolean) => void
    generateAction: () => Promise<void>
    procedureId: string,
  }) {
  const { setShowConfirmModal, procedureId, generateAction } = props;

  const createTestParameters = useParameterStore((state) => state.createProcedureTestParameters);
  const creatingTestParameters = useParameterStore((state) => state.creatingTestParameters);

  useEffect(() => {
    void createTestParameters(procedureId);
  }, []);

  return (
    <Offcanvas
      show
      placement="end"
      className="side-panel"
      >
      <Offcanvas.Body className='side-panel-container d-flex flex-column'>
        <div>
          {creatingTestParameters && <LoadingSpinner />}
          {!creatingTestParameters && <div>
            {I18n.get('applyWarning')}
            <div className='mb-2'><ProcedureCreateParameters subjectId={procedureId} type={ParameterType.TEST_INPUT} /></div>
            <div className="d-flex justify-content-end gap-2">
              <BasicButton className="btn btn-secondary" onClick={() => setShowConfirmModal(false)} label={I18n.get('cancel')} />
              <BasicButton className='btn btn-primary' onClick={() => generateAction()} label={I18n.get('apply')} />
            </div>
          </div>}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
